import React from "react";
import {
  Accordion,
  AccordionButton,
  AccordionItem as ChakraAccordionItem,
  AccordionPanel,
  Checkbox,
  Divider,
  Flex,
  HStack,
  Icon,
  Text
} from "@chakra-ui/react";
import { IoChevronDownOutline, IoChevronUp } from "react-icons/io5";
import styles from "./Accordion.module.css";

interface AccordionItemProps {
  title: string;
  children?: React.ReactNode;
  isSelected?: boolean;
  onSelect?(): void;
  collapsedExtra?: React.ReactNode[];
  defaultExpanded?: boolean;
  allowToggle?: boolean;
  noContentPadding?: boolean;
  variant?: "gray" | "dark";
}

const AccordionItem: React.FC<AccordionItemProps> = ({
  title,
  children,
  isSelected,
  onSelect,
  collapsedExtra,
  defaultExpanded = true,
  allowToggle = true,
  noContentPadding,
  variant = "dark"
}) => {
  const [expandedIndex, setIndexExpanded] = React.useState<number>(
    defaultExpanded ? 0 : -1
  );
  const [applyOverflow, setApplyOverflow] = React.useState(false);
  React.useEffect(() => {
    // We have to reset the overflow after all transitions have elapsed,
    // so it does not obstruct the view of AutoCompletes\DatePickers dropdowns.
    const timeoutId = window.setTimeout(() => {
      if (expandedIndex === 0) {
        setApplyOverflow(false);
      }
    }, 250);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [expandedIndex]);

  const isCard = variant === "gray";

  return (
    <Accordion
      allowToggle={allowToggle}
      index={expandedIndex}
      onChange={(index: number) => {
        setIndexExpanded(index);
        if (index === -1) {
          setApplyOverflow(true);
        }
      }}
      border={isSelected ? "2px solid" : "none"}
      borderColor="dark.500"
      boxShadow="base"
      backgroundColor="white"
      m={isSelected ? 0 : "2px"}
      borderRadius="xl"
    >
      <ChakraAccordionItem
        border="none"
        className={applyOverflow ? "" : styles.accordionItem}
      >
        {({ isExpanded }) => (
          <>
            <AccordionButton
              color="gray.700"
              px={onSelect ? 8 : 6}
              py="4"
              borderRadius="xl"
              bg={isCard ? "gray.200" : "initial"}
              _expanded={
                isCard
                  ? { borderBottomRadius: "none" }
                  : {
                      bg: "dark.500",
                      color: "white",
                      borderBottomRadius: "none"
                    }
              }
              {...(isCard && { _hover: { bg: "gray.200" } })}
            >
              <Flex
                alignItems="center"
                color={isCard ? "black" : "inherit"}
                flex="1"
                textAlign="left"
                fontSize="xl"
                fontWeight={isCard ? "semibold" : "bold"}
                lineHeight={7}
              >
                {onSelect && (
                  <Checkbox
                    colorScheme="dark"
                    borderColor="dark.500"
                    size="lg"
                    mr={3}
                    onChange={onSelect}
                  />
                )}
                {title}
                {!isExpanded &&
                  collapsedExtra?.map((text, i) => (
                    <HStack
                      key={i}
                      ml={6}
                      spacing={6}
                      color="black"
                      fontSize="sm"
                      fontWeight="medium"
                      lineHeight={5}
                    >
                      <Text>{text}</Text>
                      {i !== collapsedExtra?.length - 1 && (
                        <Divider
                          borderColor="gray.300"
                          orientation="vertical"
                          h="7"
                          mr={6}
                        />
                      )}
                    </HStack>
                  ))}
              </Flex>
              {allowToggle ? (
                isExpanded ? (
                  <Icon
                    as={IoChevronUp}
                    boxSize="6"
                    color={isCard ? "black" : "white"}
                  />
                ) : (
                  <Icon
                    as={IoChevronDownOutline}
                    boxSize="6"
                    color={isCard ? "black" : "gray.500"}
                  />
                )
              ) : null}
            </AccordionButton>
            <AccordionPanel
              p={
                noContentPadding
                  ? 0
                  : isCard
                  ? "24px 16px"
                  : { base: "6", lg: "8" }
              }
            >
              {children}
            </AccordionPanel>
          </>
        )}
      </ChakraAccordionItem>
    </Accordion>
  );
};

export default AccordionItem;
